import _ from 'lodash';

/**
 * Coerces a version to a number, treating absent values as unusable.
 * Number(null) and Number('') are both 0, which would read as a real version 0 and make
 * every sibling look newer than it is, so those have to become NaN explicitly.
 */
function toVersion(value) {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(value);
}

/**
 * Normalizes version metadata on the project_templates of each project type.
 *
 * `num_versions` is the HIGHEST live version number in a group, not a count of versions.
 * Those two numbers diverge permanently the moment anyone discards a draft: the API numbers
 * new versions off `with_deleted.maximum(:version) + 1` and a unique index on
 * (group_identifier, version) means a number is never reused. So a group can hold 7 live
 * templates whose newest is version 9.
 *
 * Comparing a count against a version ordinal is what broke this before, and it failed in
 * both directions: version 9 of a 7-template group reported as stale and lost its
 * PUBLISH/DISCARD controls, while the published version 7 of that same group reported as
 * newest (7 === 7, by coincidence) and offered NEW VERSION, minting versions on top of an
 * existing draft.
 */
export function normalizeProjectTemplateVersions(projectTypes) {
  if (!projectTypes || !Array.isArray(projectTypes)) return projectTypes;
  projectTypes.forEach(pt => {
    if (!pt.project_templates || !pt.project_templates.length) return;
    const byGroup = _.groupBy(pt.project_templates, t =>
      t.group_identifier != null && t.group_identifier !== '' ? t.group_identifier : 'template_' + (t.id != null ? t.id : '')
    );
    Object.keys(byGroup).forEach(gid => {
      const group = byGroup[gid];
      const versions = group.map(t => toVersion(t.version)).filter(v => !Number.isNaN(v));
      // No parseable version anywhere in the group: leave both fields alone rather than
      // write a bogus max that would make every version in the group look stale.
      if (!versions.length) return;
      const maxVersion = Math.max(...versions);
      group.forEach(t => {
        t.num_versions = maxVersion;
        // The API's `latest` is authoritative and accounts for deleted versions. Only
        // derive it when the response genuinely omitted it.
        if (t.latest === undefined) {
          t.latest = toVersion(t.version) === maxVersion;
        }
      });
    });
  });
  return projectTypes;
}

/**
 * Highest version number among the templates sharing `template`'s group.
 *
 * Prefers the loaded group because it is the only source that reflects discards made this
 * session. Falls back to `num_versions` (also a max, never a count) when the group isn't
 * loaded, then to the template's own version so a lone template still reads as newest.
 */
export function maxVersionInGroup(template, groupTemplates) {
  if (!template) return null;
  const ownVersion = toVersion(template.version);

  // Only trust the group list when we can actually key on a group. Matching on a null
  // group_identifier would lump together unrelated templates.
  if (template.group_identifier != null && template.group_identifier !== '') {
    const versions = (groupTemplates || [])
      .filter(t => t && t.group_identifier === template.group_identifier)
      .map(t => toVersion(t.version))
      .filter(v => !Number.isNaN(v));
    if (versions.length) return Math.max(...versions);
  }

  const numVersions = toVersion(template.num_versions);
  if (!Number.isNaN(numVersions) && numVersions > 0) return numVersions;
  return Number.isNaN(ownVersion) ? null : ownVersion;
}

/**
 * Whether `template` is the newest version of its group, derived from what the client has
 * loaded. Use only as a fallback when the API omitted its authoritative `latest` flag,
 * since only the server knows about versions discarded by other people.
 *
 * Coerces before comparing: JSON:API can deliver `version` as a string, and a strict
 * compare against a number would report every version as stale.
 */
export function isLatestVersion(template, groupTemplates) {
  if (!template) return false;
  const ownVersion = toVersion(template.version);
  if (Number.isNaN(ownVersion)) return false;
  return ownVersion === maxVersionInGroup(template, groupTemplates);
}

/**
 * Picks the version produced by a queued new-version create.
 *
 * A backgrounded create returns 202 with only a group_identifier, so the caller polls the
 * group and asks this which row is the new one: the highest version above the one it branched
 * from. Returns null while the job has not landed yet.
 *
 * Highest rather than "branchedFrom + 1" on purpose, because version numbers skip discarded
 * drafts -- branching from version 7 of a group that already reached 8 produces version 9.
 */
export function pickCreatedVersion(templates, branchedFromVersion) {
  const floor = toVersion(branchedFromVersion);
  if (!Array.isArray(templates) || Number.isNaN(floor)) return null;
  const newer = templates
    .filter(t => t && !Number.isNaN(toVersion(t.version)) && toVersion(t.version) > floor)
    .sort((a, b) => toVersion(b.version) - toVersion(a.version));
  return newer.length ? newer[0] : null;
}

export default normalizeProjectTemplateVersions;



// WEBPACK FOOTER //
// ./src/containers/ProjectTemplatesContainer/ProjectTypesNav/utils/normalizeProjectTemplateVersions.js